import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Package, Truck, ExternalLink, RefreshCw, Loader2, RotateCcw, Copy, Check, Zap, Shield, FileWarning } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOrderDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const { formatPrice, currency, exchangeRate } = useCurrency();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  // Shipping/insurance amounts are stored canonically in MXN (returned by Envia.com).
  // When the user toggle is USD, convert using the live Banxico rate; otherwise show MXN.
  const formatShippingPrice = (priceMXN) => {
    if (!priceMXN) return 'Gratis';
    if (currency === 'USD' && exchangeRate?.value) {
      return `$${(priceMXN / exchangeRate.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    }
    return `$${priceMXN.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
  };
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [newPaymentStatus, setNewPaymentStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [newShippingStatus, setNewShippingStatus] = useState('pending');
  const [savingShipping, setSavingShipping] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const [returnRequest, setReturnRequest] = useState(null);

  // ---- Regenerar Guía (cuando se perdió el shipment original) ----
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenZip, setRegenZip] = useState('');
  const [regenCity, setRegenCity] = useState('');
  const [regenState, setRegenState] = useState('');

  useEffect(() => {
    axios.get(`${API}/orders/${id}`, { headers })
      .then(r => {
        setOrder(r.data);
        setNewStatus(r.data.order_status);
        setNewPaymentStatus(r.data.payment_status);
        setTrackingNumber(r.data.tracking_number || '');
        setNewShippingStatus(r.data.shipping_status || 'pending');
        setLoading(false);
      }).catch(() => { setLoading(false); navigate('/admin/ordenes'); });
    // Cargar devolución asociada (si existe)
    axios.get(`${API}/admin/returns/by-order/${id}`, { headers })
      .then(r => setReturnRequest(r.data || null))
      .catch(() => setReturnRequest(null));
  }, [id, token, navigate]);

  const handleUpdate = async () => {
    try {
      const res = await axios.put(`${API}/orders/${id}/status`, {
        order_status: newStatus,
        payment_status: newPaymentStatus,
        tracking_number: trackingNumber
      }, { headers });
      setOrder(res.data);
      toast.success('Orden actualizada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleUpdateShippingStatus = async () => {
    setSavingShipping(true);
    try {
      const res = await axios.patch(`${API}/orders/${id}/shipping-status`, {
        shipping_status: newShippingStatus,
      }, { headers });
      setOrder(res.data);
      toast.success('Estado de envío actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSavingShipping(false);
    }
  };

  const handleLiveTrack = async () => {
    if (!order?.envia_tracking_number) return;
    setTrackLoading(true);
    setTracking(null);
    try {
      const res = await axios.get(`${API}/envia/track/${encodeURIComponent(order.envia_tracking_number)}`);
      setTracking(res.data);
      if (!res.data.events?.length) {
        toast.message(res.data.message || 'Sin eventos aún');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error consultando rastreo');
    } finally {
      setTrackLoading(false);
    }
  };

  const handleCopyTracking = async () => {
    if (!order?.envia_tracking_number) return;
    try {
      await navigator.clipboard.writeText(order.envia_tracking_number);
      setCopiedTracking(true);
      toast.success('Tracking copiado');
      setTimeout(() => setCopiedTracking(false), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  // ---- Regenerar Guía ----
  // Opens the dialog and tries to pre-fill ZIP from the free-text
  // shipping_address (first 5-digit number found). City/state must be filled
  // by the admin since they're not stored structured on the order document.
  const openRegenerateDialog = () => {
    const addr = order?.shipping_address || '';
    const zipMatch = addr.match(/\b\d{5}\b/);
    setRegenZip(zipMatch ? zipMatch[0] : '');
    setRegenCity('');
    setRegenState('');
    setRegenOpen(true);
  };

  const handleRegenerateShipment = async () => {
    if (!regenZip || regenZip.length !== 5) {
      toast.error('Ingresa un CP válido de 5 dígitos');
      return;
    }
    if (!regenCity.trim() || !regenState.trim()) {
      toast.error('Ciudad y estado son requeridos');
      return;
    }
    setRegenLoading(true);
    try {
      const body = {
        carrier: order.envia_carrier,
        service: order.envia_service || '',
        total_price: Number(order.envia_price_mxn || order.shipping_cost_mxn || 0),
        destination_zip: regenZip.trim(),
        destination_city: regenCity.trim(),
        destination_state: regenState.trim(),
        destination_name: order.user_name || 'Cliente',
        destination_phone: order.user_phone || '',
        weight: 1.0,
        length: 20.0,
        width: 20.0,
        height: 20.0,
        insure_shipment: !!order.insured,
        declared_value: Number(order.declared_value || 0),
      };
      const res = await axios.post(`${API}/envia/create-shipment`, body, { headers });
      const data = res.data || {};
      const trackingNum = data.tracking_number;
      const labelUrl = data.label_url;
      const shipmentId = data.shipment_id;

      if (!trackingNum) {
        toast.error(data.message || 'Envia no devolvió un número de rastreo');
        return;
      }

      // Persist the new tracking + label_url + shipment_id on the order via
      // the admin status endpoint (the only endpoint that accepts these fields
      // on an existing order).
      await axios.put(
        `${API}/orders/${id}/status`,
        {
          order_status: order.order_status,
          payment_status: order.payment_status,
          tracking_number: trackingNum,
        },
        { headers }
      );

      // Reload the order so the UI picks up the new envia_* fields (the
      // shipment metadata is mirrored via the admin endpoint above; full
      // envia_* propagation depends on backend, so we refetch).
      const fresh = await axios.get(`${API}/orders/${id}`, { headers });
      // Patch in-memory for instant feedback in case the backend didn't
      // sync envia_label_url back (the value from Envia is the canonical one).
      setOrder({
        ...fresh.data,
        envia_tracking_number: trackingNum,
        envia_label_url: labelUrl || fresh.data.envia_label_url,
        envia_shipment_id: shipmentId || fresh.data.envia_shipment_id,
        shipping_status:
          fresh.data.shipping_status === 'pending'
            ? 'label_generated'
            : fresh.data.shipping_status,
      });
      setTrackingNumber(trackingNum);
      setNewShippingStatus(
        fresh.data.shipping_status === 'pending' ? 'label_generated' : fresh.data.shipping_status
      );

      toast.success(`Guía regenerada · Tracking: ${trackingNum}`);
      setRegenOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error regenerando guía';
      toast.error(typeof msg === 'string' ? msg : 'Error regenerando guía');
    } finally {
      setRegenLoading(false);
    }
  };

  if (loading) return <div className="py-8 text-center text-muted-foreground">Cargando...</div>;
  if (!order) return null;

  const statusLabels = { pendiente: 'Pendiente', pagado: 'Pagado', cancelado: 'Cancelado', en_proceso: 'En Proceso', enviado: 'Enviado', entregado: 'Entregado', compra_interna: 'Compra Interna' };

  const shippingMeta = {
    pending:         { label: 'Pendiente',     className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
    label_generated: { label: 'Guía Generada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
    in_transit:      { label: 'En Tránsito',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
    delivered:       { label: 'Entregado',     className: 'bg-green-500/15 text-green-300 border-green-500/30' },
    failed:          { label: 'Fallido',       className: 'bg-red-500/15 text-red-300 border-red-500/30' },
  };
  const currentShippingMeta = shippingMeta[order.shipping_status] || shippingMeta.pending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/ordenes')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-2xl font-display font-semibold">Orden {order.order_number}</h1>
          <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleString('es-MX')}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-display font-semibold mb-4">Productos</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="px-2 py-2 text-left">Producto</th><th className="px-2 py-2 text-left">SKU</th><th className="px-2 py-2 text-right">Cant.</th><th className="px-2 py-2 text-right">Precio</th><th className="px-2 py-2 text-right">Total</th></tr></thead>
                <tbody>
                  {order.items?.map((item, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-2 py-2">{item.name}</td>
                      <td className="px-2 py-2 font-mono text-xs">{item.sku}</td>
                      <td className="px-2 py-2 text-right">{item.quantity}</td>
                      <td className="px-2 py-2 text-right">{formatPrice(item.product?.price ?? item.unit_price ?? item.price ?? 0)}</td>
                      <td className="px-2 py-2 text-right font-medium">{formatPrice((item.product?.price ?? item.unit_price ?? item.price ?? 0) * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 space-y-1 text-sm text-right" data-testid="admin-order-summary-mxn">
                {(() => {
                  // Prefer MXN bucket persisted at checkout. Fallback to legacy fields.
                  const subtotalMXN = order.subtotal_mxn ?? (order.subtotal_usd && order.exchange_rate_used
                    ? order.subtotal_usd * order.exchange_rate_used
                    : order.subtotal);
                  const discountMXN = order.discount_mxn ?? (order.discount_amount && order.exchange_rate_used
                    ? order.discount_amount * order.exchange_rate_used
                    : order.discount_amount);
                  const shippingMXN = order.shipping_cost_mxn ?? order.envia_price_mxn ?? order.shipping_cost ?? 0;
                  const insuranceMXN = order.insurance_cost_mxn ?? 0;
                  const totalMXN = order.total_mxn ?? order.total ?? 0;
                  const fmtMXN = (n) =>
                    `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
                  return (
                    <>
                      <div>Subtotal: <span data-testid="admin-subtotal-mxn">{fmtMXN(subtotalMXN)}</span></div>
                      {discountMXN > 0 && (
                        <div className="text-green-400">
                          Descuento ({order.discount_info?.percent}%): -{fmtMXN(discountMXN)}
                        </div>
                      )}
                      <div>
                        Envío:&nbsp;
                        <span data-testid="admin-order-shipping-mxn">
                          {shippingMXN > 0 ? fmtMXN(shippingMXN) : 'Gratis'}
                        </span>
                      </div>
                      {order.insured && insuranceMXN > 0 && (
                        <div className="text-blue-300 flex items-center justify-end gap-1.5" data-testid="admin-order-insurance-line">
                          <Shield className="h-3 w-3" />
                          Seguro: {fmtMXN(insuranceMXN)}
                        </div>
                      )}
                      <div className="text-lg font-display font-semibold border-t pt-2">
                        Total: <span data-testid="admin-total-mxn">{fmtMXN(totalMXN)}</span>
                      </div>
                      {order.exchange_rate_used ? (
                        <p
                          className="text-[11px] text-muted-foreground italic leading-snug"
                          data-testid="admin-order-rate-note"
                        >
                          Tipo de cambio usado: ${Number(order.exchange_rate_used).toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} MXN/USD
                          {order.subtotal_usd ? ` · Subtotal original: $${Number(order.subtotal_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD` : ''}
                        </p>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="font-display font-semibold mb-3">Cliente</h3>
              <div className="text-sm space-y-1">
                <div><strong>Nombre:</strong> {order.user_name}</div>
                <div><strong>Email:</strong> {order.user_email}</div>
                <div><strong>Tipo:</strong> {order.user_type === 'internal_buyer' ? 'Comprador Interno' : order.user_type === 'admin' ? 'Administrador' : 'Cliente'}</div>
                {order.shipping_address && <div><strong>Dir. Envío:</strong> {order.shipping_address}</div>}
                {order.notes && <div><strong>Notas:</strong> {order.notes}</div>}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-display font-semibold">Actualizar Estado</h3>
              <div className="space-y-2">
                <Label>Estado de Orden</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="admin-orders-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado de Pago</Label>
                <Select value={newPaymentStatus} onValueChange={setNewPaymentStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="pagado">Pagado</SelectItem>
                    <SelectItem value="no_aplica">No Aplica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Número de Rastreo</Label>
                <Input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Número de guía" />
              </div>
              <Button onClick={handleUpdate} className="w-full">Actualizar Orden</Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><Truck className="h-4 w-4" /> Info de Envío</h3>
              {order.shipping_info ? (
                <div className="text-sm space-y-1 text-muted-foreground">
                  {order.shipping_info.name && <div>{order.shipping_info.name}</div>}
                  {order.shipping_info.carrier && <div>Carrier: <span className="text-foreground">{order.shipping_info.carrier}</span></div>}
                  {order.shipping_info.estimated_delivery && <div>{order.shipping_info.estimated_delivery}</div>}
                  {typeof order.shipping_info.cost === 'number' && <div>Costo: {formatShippingPrice(order.shipping_info.cost)}</div>}
                </div>
              ) : <div className="text-sm text-muted-foreground">Sin info de envío</div>}
            </CardContent>
          </Card>

          {/* ---- PROMINENT ENVIA.COM CARD (blue accent) ---- */}
          {order.envia_carrier && (
            <Card className="border-2 border-blue-500/40 bg-blue-500/5 shadow-[0_0_0_1px_rgba(59,130,246,0.15)]" data-testid="admin-envia-card">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display font-semibold flex items-center gap-2 text-blue-300">
                    <Zap className="h-4 w-4 fill-blue-400 text-blue-400" />
                    Envío Envia.com
                  </h3>
                  <Badge variant="outline" className={currentShippingMeta.className} data-testid="admin-shipping-status-badge">
                    {currentShippingMeta.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  {order.envia_carrier && (
                    <div className="space-y-0.5">
                      <p className="uppercase tracking-wide text-muted-foreground text-[10px] font-medium">Paquetería</p>
                      <p className="font-medium text-sm">{order.envia_carrier.toUpperCase()}</p>
                    </div>
                  )}
                  {order.envia_service && (
                    <div className="space-y-0.5">
                      <p className="uppercase tracking-wide text-muted-foreground text-[10px] font-medium">Servicio</p>
                      <p className="font-medium text-sm">{order.envia_service}</p>
                    </div>
                  )}
                  {typeof order.envia_price_mxn === 'number' && (
                    <div className="space-y-0.5">
                      <p className="uppercase tracking-wide text-muted-foreground text-[10px] font-medium">Precio</p>
                      <p className="font-medium text-sm">{formatShippingPrice(order.envia_price_mxn)}</p>
                    </div>
                  )}
                  {order.envia_shipment_id && (
                    <div className="space-y-0.5">
                      <p className="uppercase tracking-wide text-muted-foreground text-[10px] font-medium">Shipment ID</p>
                      <code className="font-mono text-xs">{order.envia_shipment_id}</code>
                    </div>
                  )}
                </div>

                {order.envia_tracking_number && (
                  <div className="space-y-1.5">
                    <p className="uppercase tracking-wide text-muted-foreground text-[10px] font-medium">Número de Rastreo</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 rounded-md bg-blue-950/40 border border-blue-500/30 text-sm font-mono break-all text-blue-200" data-testid="admin-tracking-number">
                        {order.envia_tracking_number}
                      </code>
                      <Button variant="outline" size="sm" onClick={handleCopyTracking} className="shrink-0 gap-1.5 border-blue-500/40 hover:bg-blue-500/10" data-testid="admin-copy-tracking-button">
                        {copiedTracking ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        {copiedTracking ? 'Copiado' : 'Copiar'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {order.envia_label_url && (
                    <a href={order.envia_label_url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[140px]" data-testid="admin-label-link">
                      <Button variant="secondary" size="sm" className="w-full gap-2">
                        <ExternalLink className="h-3.5 w-3.5" /> Etiqueta PDF
                      </Button>
                    </a>
                  )}
                  {order.envia_tracking_number && (
                    <Button variant="outline" size="sm" onClick={handleLiveTrack} disabled={trackLoading} className="flex-1 min-w-[140px] gap-2 border-blue-500/40 hover:bg-blue-500/10" data-testid="admin-track-button">
                      {trackLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Rastreo en vivo
                    </Button>
                  )}
                  {/* ---- Regenerar Guía: only when carrier exists but tracking is missing AND shipping is still pending ---- */}
                  {order.envia_carrier
                    && !order.envia_tracking_number
                    && order.shipping_status === 'pending' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={openRegenerateDialog}
                      disabled={regenLoading}
                      className="flex-1 min-w-[160px] gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="admin-regenerate-shipment-button"
                    >
                      {regenLoading
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <FileWarning className="h-3.5 w-3.5" />}
                      {regenLoading ? 'Regenerando…' : 'Regenerar Guía'}
                    </Button>
                  )}
                </div>

                <div className="space-y-2 pt-3 border-t border-blue-500/20">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Cambiar estado de envío</Label>
                  <div className="flex gap-2">
                    <Select value={newShippingStatus} onValueChange={setNewShippingStatus}>
                      <SelectTrigger data-testid="admin-shipping-status-select" className="flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="label_generated">Guía Generada</SelectItem>
                        <SelectItem value="in_transit">En Tránsito</SelectItem>
                        <SelectItem value="delivered">Entregado</SelectItem>
                        <SelectItem value="failed">Fallido</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={handleUpdateShippingStatus} disabled={savingShipping} data-testid="admin-save-shipping-status">
                      {savingShipping ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Al guardar se enviará un email automático al cliente (in_transit/delivered/failed).</p>
                </div>

                {tracking && (
                  <div className="border-t border-blue-500/20 pt-3 space-y-2" data-testid="admin-tracking-results">
                    {tracking.current_status && (
                      <p className="text-xs"><span className="text-muted-foreground">Estado en Envia: </span><span className="font-medium">{tracking.current_status}</span></p>
                    )}
                    {tracking.events?.length > 0 ? (
                      <ol className="space-y-2 text-xs max-h-60 overflow-auto pr-1">
                        {tracking.events.map((ev, i) => (
                          <li key={i} className="border-l-2 border-blue-500/30 pl-3 py-1">
                            {ev.description && <p className="font-medium">{ev.description}</p>}
                            <p className="text-muted-foreground">
                              {ev.location ? `${ev.location} • ` : ''}
                              {ev.timestamp ? new Date(ev.timestamp).toLocaleString('es-MX') : ''}
                            </p>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">{tracking.message || 'Sin eventos aún'}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {returnRequest && (() => {
            const returnMeta = {
              requested: { label: 'Solicitada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
              approved:  { label: 'Aprobada',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
              rejected:  { label: 'Rechazada',  className: 'bg-red-500/15 text-red-300 border-red-500/30' },
              completed: { label: 'Completada', className: 'bg-green-500/15 text-green-300 border-green-500/30' },
            }[returnRequest.status];
            const reasonLabels = {
              producto_danado: 'Producto dañado',
              producto_incorrecto: 'Producto incorrecto',
              no_cumple_expectativas: 'No cumple expectativas',
              otro: 'Otro',
            };
            return (
              <Card data-testid="admin-return-card">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display font-semibold flex items-center gap-2"><RotateCcw className="h-4 w-4" /> Devolución</h3>
                    {returnMeta && (
                      <Badge variant="outline" className={returnMeta.className} data-testid="admin-return-status-badge">
                        {returnMeta.label}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs space-y-1.5">
                    <div><span className="text-muted-foreground">Motivo:</span> {reasonLabels[returnRequest.reason] || returnRequest.reason}</div>
                    {returnRequest.comments && (
                      <div><span className="text-muted-foreground">Comentarios:</span> {returnRequest.comments}</div>
                    )}
                    {returnRequest.admin_notes && (
                      <div><span className="text-muted-foreground">Notas internas:</span> {returnRequest.admin_notes}</div>
                    )}
                    {returnRequest.envia_return_tracking && (
                      <div>
                        <span className="text-muted-foreground">Guía retorno:</span>{' '}
                        <code className="font-mono">{returnRequest.envia_return_tracking}</code>
                      </div>
                    )}
                    <div className="text-muted-foreground pt-1">
                      Solicitada el {new Date(returnRequest.created_at).toLocaleString('es-MX')}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => navigate('/admin/devoluciones')} data-testid="admin-go-to-returns">
                    Gestionar devolución
                  </Button>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      </div>

      {/* ---- Regenerar Guía dialog ---- */}
      <Dialog open={regenOpen} onOpenChange={(open) => !regenLoading && setRegenOpen(open)}>
        <DialogContent data-testid="regenerate-shipment-dialog">
          <DialogHeader>
            <DialogTitle>Regenerar guía de envío</DialogTitle>
            <DialogDescription>
              Genera una nueva guía con <strong>{(order?.envia_carrier || '').toUpperCase()}</strong>
              {order?.envia_service ? <> · <span className="opacity-80">{order.envia_service}</span></> : null}
              {' '}usando los datos de destino del pedido.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              <div className="font-medium text-foreground mb-1">Dirección registrada</div>
              <div className="line-clamp-3">{order?.shipping_address || '—'}</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="regen-zip">Código Postal (CP)</Label>
                <Input
                  id="regen-zip"
                  value={regenZip}
                  onChange={(e) => setRegenZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="64000"
                  maxLength={5}
                  inputMode="numeric"
                  disabled={regenLoading}
                  data-testid="regen-zip-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="regen-state">Estado</Label>
                <Input
                  id="regen-state"
                  value={regenState}
                  onChange={(e) => setRegenState(e.target.value)}
                  placeholder="Nuevo León"
                  disabled={regenLoading}
                  data-testid="regen-state-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="regen-city">Ciudad / Municipio</Label>
                <Input
                  id="regen-city"
                  value={regenCity}
                  onChange={(e) => setRegenCity(e.target.value)}
                  placeholder="Monterrey"
                  disabled={regenLoading}
                  data-testid="regen-city-input"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Se generará una guía real con el carrier y servicio originalmente cotizados. Si Envia.com
              rechaza la solicitud (saldo insuficiente, dirección inválida, etc.) verás el mensaje
              exacto en un toast.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRegenOpen(false)}
              disabled={regenLoading}
              data-testid="regen-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRegenerateShipment}
              disabled={regenLoading || !regenZip || !regenCity || !regenState}
              className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="regen-confirm-button"
            >
              {regenLoading
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <FileWarning className="h-4 w-4" />}
              {regenLoading ? 'Regenerando…' : 'Generar guía'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
